import { useState, useRef, useEffect } from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  PenTool,
  Calendar,
  Type,
  Trash2,
  Save,
  ZoomIn,
  ZoomOut,
  Loader2,
  Move
} from "lucide-react";
import api from "../lib/api";

const FIELD_TYPES = [
  { id: "signature", label: "Signature", icon: PenTool, color: "bg-blue-500", width: 200, height: 50 },
  { id: "date", label: "Date", icon: Calendar, color: "bg-green-500", width: 120, height: 30 },
  { id: "text", label: "Texte", icon: Type, color: "bg-purple-500", width: 150, height: 30 }
];

export default function PDFFieldEditor({ documentId, documentName, initialFields = [], onSave, onCancel }) {
  const containerRef = useRef(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fields, setFields] = useState(initialFields);
  const [zoom, setZoom] = useState(100);
  const [saving, setSaving] = useState(false);
  const [draggingField, setDraggingField] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  useEffect(() => {
    loadPDF();
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [documentId]);

  const loadPDF = async () => {
    try {
      const response = await api.get(`/documents/${documentId}/preview`, {
        responseType: "blob"
      });
      const url = URL.createObjectURL(response.data);
      setPdfUrl(url);
    } catch (err) {
      console.error("Failed to load PDF:", err);
    } finally {
      setLoading(false);
    }
  };

  const addField = (type) => {
    const fieldType = FIELD_TYPES.find(t => t.id === type);
    const newField = {
      id: `field-${Date.now()}`,
      type: type,
      label: fieldType?.label || "Champ",
      page: 1,
      x: 100,
      y: 100 + fields.length * 70,
      width: fieldType?.width || 150,
      height: fieldType?.height || 40
    };
    setFields([...fields, newField]);
  };

  const removeField = (fieldId) => {
    setFields(fields.filter(f => f.id !== fieldId));
  };

  const handleMouseDown = (e, field) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setDragOffset({
      x: clientX - rect.left,
      y: clientY - rect.top
    });
    setDraggingField(field.id);
  };

  const handleTouchStart = (e, field) => {
    e.preventDefault();
    const touch = e.touches[0];
    const rect = e.currentTarget.getBoundingClientRect();
    setDragOffset({
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    });
    setDraggingField(field.id);
  };

  const handleMouseMove = (e) => {
    if (!draggingField || !containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const field = fields.find(f => f.id === draggingField);
    if (!field) return;

    const newX = Math.max(0, Math.min(
      (e.clientX - containerRect.left - dragOffset.x) * 100 / zoom,
      612 - field.width
    ));
    const newY = Math.max(0, Math.min(
      (e.clientY - containerRect.top - dragOffset.y) * 100 / zoom,
      792 - field.height
    ));

    setFields(fields.map(f =>
      f.id === draggingField
        ? { ...f, x: Math.round(newX), y: Math.round(newY) }
        : f
    ));
  };

  const handleMouseUp = () => {
    setDraggingField(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(fields);
    } finally {
      setSaving(false);
    }
  };

  const getFieldTypeInfo = (type) => FIELD_TYPES.find(t => t.id === type);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="pdf-field-editor">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 sm:p-4 border-b bg-muted/50">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs sm:text-sm font-medium w-full sm:w-auto">Ajouter :</span>
          {FIELD_TYPES.map((type) => (
            <Button
              key={type.id}
              variant="outline"
              size="sm"
              className="text-xs sm:text-sm h-8 px-2 sm:px-3"
              onClick={() => addField(type.id)}
              data-testid={`add-field-${type.id}`}
            >
              <type.icon className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
              <span className="hidden xs:inline">{type.label}</span>
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-2 self-end sm:self-auto">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => setZoom(Math.max(50, zoom - 25))}
            disabled={zoom <= 50}
          >
            <ZoomOut className="w-3 h-3 sm:w-4 sm:h-4" />
          </Button>
          <span className="text-xs sm:text-sm w-10 sm:w-12 text-center">{zoom}%</span>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => setZoom(Math.min(150, zoom + 25))}
            disabled={zoom >= 150}
          >
            <ZoomIn className="w-3 h-3 sm:w-4 sm:h-4" />
          </Button>
        </div>
      </div>

      {/* Editor Area */}
      <div
        className="flex-1 overflow-auto p-2 sm:p-4 bg-gray-200"
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchMove={(e) => {
          const touch = e.touches[0];
          handleMouseMove({ clientX: touch.clientX, clientY: touch.clientY });
        }}
        onTouchEnd={handleMouseUp}
      >
        <div
          ref={containerRef}
          className="relative mx-auto bg-white shadow-lg"
          style={{
            width: `${612 * zoom / 100}px`,
            minHeight: `${792 * zoom / 100}px`
          }}
        >
          {/* PDF Background */}
          {pdfUrl && (
            <iframe
              src={pdfUrl}
              className="w-full h-full absolute inset-0 pointer-events-none"
              style={{ minHeight: `${792 * zoom / 100}px` }}
              title="PDF Preview"
            />
          )}

          {/* Draggable Fields */}
          {fields.map((field) => {
            const typeInfo = getFieldTypeInfo(field.type);
            return (
              <div
                key={field.id}
                className={`absolute border-2 border-dashed rounded flex items-center justify-center gap-1 text-white text-xs font-medium select-none ${typeInfo?.color || 'bg-gray-500'} ${draggingField === field.id ? 'cursor-grabbing opacity-90' : 'cursor-grab'}`}
                style={{
                  left: `${field.x * zoom / 100}px`,
                  top: `${field.y * zoom / 100}px`,
                  width: `${field.width * zoom / 100}px`,
                  height: `${field.height * zoom / 100}px`,
                  opacity: 0.85,
                  zIndex: draggingField === field.id ? 100 : 10
                }}
                onMouseDown={(e) => handleMouseDown(e, field)}
                data-testid={`field-${field.id}`}
              >
                <Move className="w-3 h-3" />
                {typeInfo && <typeInfo.icon className="w-3 h-3" />}
                <span>{field.label}</span>
                <button
                  className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeField(field.id);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Field List */}
      <div className="p-4 border-t bg-muted/50">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium">
            {fields.length} champ(s) positionné(s)
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onCancel}>
              Annuler
            </Button>
            <Button onClick={handleSave} disabled={saving || fields.length === 0} data-testid="save-fields-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer les positions
            </Button>
          </div>
        </div>
        {fields.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {fields.map((field) => {
              const typeInfo = getFieldTypeInfo(field.type);
              return (
                <Badge
                  key={field.id}
                  variant="secondary"
                  className="flex items-center gap-1"
                >
                  {typeInfo && <typeInfo.icon className="w-3 h-3" />}
                  {field.label} (X:{field.x}, Y:{field.y})
                </Badge>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
